import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { Button, Paragraph, Small } from "@fronterahealth/frontera-ui-components";

import {
  CaregiverType,
  ProviderType,
  useCaregiverByIdQuery,
  useDisconnectCaregiverFromOrganizationMutation,
  useUpdateCaregiverMetadataMutation,
} from "@api/graphql/types-and-hooks";
import { CopyToClipboardButton } from "@components/CopyToClipboardButton/CopyToClipboardButton";
import { FormContainer } from "@components/forms/FormLayout";
import SubmitButton from "@components/forms/FormSubmitButton/FormSubmitButton";
import { useFormUtils } from "@components/forms/useFormUtils";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { InviteCaregiverModal } from "@pages/Members/components/InviteCaregiverModal";
import { CaregiverAppStatus, CodeSentVia } from "@pages/Members/types";
import { buildBcbaNameByProviderId, mapCaregiverToMemberCaregiver } from "@pages/Members/utils/caregiverMapping";
import { useAdminData } from "@providers/AdminDataProvider";
import { useProvidersData } from "@providers/ProvidersDataProvider";

interface CaregiverDetailsFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

const EMPTY_FORM: CaregiverDetailsFormData = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
};

const codeSentViaLabel: Record<CodeSentVia, string> = {
  [CodeSentVia.Both]: "Email & SMS",
  [CodeSentVia.Sms]: "SMS",
  [CodeSentVia.Email]: "Email",
};

const statusConfig: Record<CaregiverAppStatus, { label: string; dot: string; bg: string; text: string }> = {
  [CaregiverAppStatus.NotInvited]: {
    label: "Not invited",
    dot: "bg-limestone-400",
    bg: "bg-limestone-50",
    text: "text-text-secondary",
  },
  [CaregiverAppStatus.CodeSent]: { label: "Code sent", dot: "bg-amber-400", bg: "bg-amber-50", text: "text-amber-700" },
  [CaregiverAppStatus.Connected]: {
    label: "Connected",
    dot: "bg-green-500",
    bg: "bg-green-50",
    text: "text-green-700",
  },
};

export const CaregiverDetailsTab: React.FC = () => {
  const { caregiverId } = useParams<{ caregiverId: string }>();
  const { orgId } = useAdminData();
  const queryClient = useQueryClient();
  const [inviteModalOpen, setInviteModalOpen] = useState(false);

  const { providersQuery } = useProvidersData();

  const caregiverDetailsQuery = useCaregiverByIdQuery(
    { caregiverId: caregiverId || "" },
    {
      queryKey: ["caregiver-by-id", caregiverId],
      enabled: !!caregiverId,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  const bcbaNameByProviderId = useMemo(
    () => buildBcbaNameByProviderId((providersQuery.data?.provider || []) as ProviderType[]),
    [providersQuery.data],
  );

  const caregiver = useMemo(
    () =>
      mapCaregiverToMemberCaregiver(
        caregiverDetailsQuery.data?.caregiverById as CaregiverType | undefined,
        bcbaNameByProviderId,
      ),
    [caregiverDetailsQuery.data?.caregiverById, bcbaNameByProviderId],
  );

  const updateCaregiverMetadata = useUpdateCaregiverMetadataMutation({
    onSuccess: (res) => {
      if (!res.updateCaregiverMetadata?.caregiverMetadata) {
        notifyError("Something went wrong updating this caregiver.");
        return;
      }
      notifySuccess("Caregiver information updated!");
      caregiverDetailsQuery.refetch();
      queryClient.invalidateQueries({ queryKey: ["getCaregiversDataQuery"] });
    },
    onError: () => {
      notifyError("Something went wrong updating this caregiver.");
    },
  });

  const disconnectCaregiver = useDisconnectCaregiverFromOrganizationMutation({
    onSuccess: (res) => {
      if (!res.disconnectCaregiverFromOrganization?.success) {
        notifyError(res.disconnectCaregiverFromOrganization?.message || "Something went wrong revoking access.");
        return;
      }
      notifySuccess(res.disconnectCaregiverFromOrganization?.message || "Access revoked.");
      caregiverDetailsQuery.refetch();
    },
    onError: () => {
      notifyError("Something went wrong revoking access.");
    },
  });

  const { formState, onSubmit, reset, RegisteredFormInput } = useFormUtils<CaregiverDetailsFormData>({
    defaultValues: EMPTY_FORM,
    mutationFn: async (values) => {
      if (!caregiver) return;
      await updateCaregiverMetadata.mutateAsync({
        caregiver: caregiver.id,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email || undefined,
        phoneNumber1: values.phone || undefined,
      });
    },
  });

  useEffect(() => {
    if (caregiver) {
      reset({
        firstName: caregiver.firstName,
        lastName: caregiver.lastName,
        email: caregiver.email,
        phone: caregiver.phone,
      });
    }
  }, [caregiver, reset]);

  if (!caregiver) {
    if (caregiverDetailsQuery.isFetching) return null;
    return <div className="text-text-secondary">Caregiver not found.</div>;
  }

  const handleSendInvite = () => {
    setInviteModalOpen(true);
  };

  const handleResend = () => {
    setInviteModalOpen(true);
  };

  const handleRevokeAccess = () => {
    if (!orgId) return;
    disconnectCaregiver.mutate({ organization: orgId, caregiver: caregiver.id });
  };

  const status = statusConfig[caregiver.appStatus];

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Caregiver Information */}
      <FormContainer onSubmit={onSubmit}>
        <div className="rounded-lg border border-limestone-200 bg-white">
          <div className="border-b border-limestone-200 px-6 py-4">
            <Small displayType="loud" colorType="primary">
              Caregiver Information
            </Small>
          </div>
          <div className="px-6 py-5">
            <div className="flex flex-col gap-5">
              <div className="grid grid-cols-2 gap-4">
                <RegisteredFormInput formKey="firstName" formState={formState} label="First Name" />
                <RegisteredFormInput formKey="lastName" formState={formState} label="Last Name" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <RegisteredFormInput formKey="email" formState={formState} label="Email" required={false} />
                <RegisteredFormInput formKey="phone" formState={formState} label="Phone Number" required={false} />
              </div>
            </div>
            <div className="mt-5 flex justify-end border-t border-limestone-100 pt-4">
              <SubmitButton isLoading={formState.isSubmitting} buttonText="Update" />
            </div>
          </div>
        </div>
      </FormContainer>

      {/* App Access */}
      <div className="rounded-lg border border-limestone-200 bg-white">
        <div className="flex items-center justify-between border-b border-limestone-200 px-6 py-4">
          <Small displayType="loud" colorType="primary">
            App Access
          </Small>
          <span
            className={clsx(
              "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium",
              status.bg,
              status.text,
            )}
          >
            <span className={clsx("h-1.5 w-1.5 rounded-full", status.dot)} />
            {status.label}
          </span>
        </div>
        <div className="px-6 py-5">
          {caregiver.appStatus === CaregiverAppStatus.NotInvited && (
            <div className="flex flex-col gap-4">
              <Paragraph colorType="secondary">This caregiver hasn&apos;t been invited to the app yet.</Paragraph>
              <div>
                <Button variant="primary" buttonStyle="filled" text="Send invite" onClick={handleSendInvite} />
              </div>
            </div>
          )}

          {caregiver.appStatus === CaregiverAppStatus.CodeSent && (
            <div className="flex flex-col gap-5">
              <div className="grid grid-cols-3 gap-4">
                <div className="flex flex-col gap-1">
                  <Small colorType="secondary" className="text-xs">
                    Sent on
                  </Small>
                  <Small displayType="loud" colorType="primary">
                    {formatDate(caregiver.codeSentDate)}
                  </Small>
                </div>
                <div className="flex flex-col gap-1">
                  <Small colorType="secondary" className="text-xs">
                    Delivery method
                  </Small>
                  <Small displayType="loud" colorType="primary">
                    {codeSentViaLabel[caregiver.codeSentVia ?? CodeSentVia.Email]}
                  </Small>
                </div>
                {caregiver.activationCode && (
                  <div className="flex flex-col gap-1">
                    <Small colorType="secondary" className="text-xs">
                      Activation code
                    </Small>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-semibold tracking-wider text-text-primary">
                        {caregiver.activationCode}
                      </span>
                      <CopyToClipboardButton text={caregiver.activationCode} />
                    </div>
                  </div>
                )}
              </div>
              <div className="border-t border-limestone-100 pt-4">
                <Button variant="primary" buttonStyle="ghost" text="Resend activation code" onClick={handleResend} />
              </div>
            </div>
          )}

          {caregiver.appStatus === CaregiverAppStatus.Connected && (
            <div className="flex flex-col gap-5">
              <div className="grid grid-cols-3 gap-4">
                <div className="flex flex-col gap-1">
                  <Small colorType="secondary" className="text-xs">
                    Connected on
                  </Small>
                  <Small displayType="loud" colorType="primary">
                    {formatDate(caregiver.connectedDate)}
                  </Small>
                </div>
                {caregiver.activationCode && (
                  <div className="flex flex-col gap-1">
                    <Small colorType="secondary" className="text-xs">
                      Activation code
                    </Small>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-semibold tracking-wider text-text-primary">
                        {caregiver.activationCode}
                      </span>
                      <CopyToClipboardButton text={caregiver.activationCode} />
                    </div>
                  </div>
                )}
              </div>
              <div className="border-t border-limestone-100 pt-4">
                <Button
                  variant="negative"
                  buttonStyle="ghost"
                  text="Revoke access"
                  onClick={handleRevokeAccess}
                  disabled={disconnectCaregiver.isPending}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <InviteCaregiverModal
        isOpen={inviteModalOpen}
        onClose={() => {
          setInviteModalOpen(false);
          caregiverDetailsQuery.refetch();
        }}
        preselectedCaregiverId={caregiver.id}
      />
    </div>
  );
};
